import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { RecordOperationRequest } from '@/api/handlers/resources/operations/requests';
import type { RecordOperationResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { JournalQueryKeys } from '../journal';
import { AccountQueryKeys } from '../accounts';
import { OperationsQueryKeys } from './operationsQueryKeys';

export type RecordOperationVariables = {
    key: string;
    body: RecordOperationRequest;
};

/**
 * Post an operation.
 *
 * **This is the one write in this resource that moves the ledger**, and the
 * invalidations say so: it invalidates the journal and the chart as well as the
 * operations key, because it has just added a posted entry and moved two account
 * balances. The editor's three writes touch none of that — they change a recipe,
 * not the books.
 *
 * It does not reject. The caller branches on `result.success` and reads
 * `result.error.code`, the codebase-wide envelope contract, because most of what
 * comes back here is a *refusal* the form can anchor to a field — a closed
 * period on the date, a slot outside its filter on its dropdown — rather than a
 * failure.
 *
 * Idempotency is the caller's to key: `body.id` is generated per submission
 * attempt-series so a retry replays and a genuine second recording does not.
 */
export const useRecordOperationMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<RecordOperationResponse>,
        Error,
        RecordOperationVariables
    >({
        mutationFn: ({ key, body }) =>
            apiClient.operations.recordOperation(key, body),
        onSuccess: (result) => {
            if (!result.success) {
                return;
            }
            // A replay posted nothing, so nothing downstream moved. Invalidating
            // anyway would refetch the journal and the whole chart every time a
            // dropped response was retried.
            if (result.response.replayed) {
                return;
            }
            // `lastUsedAt` on the catalog row is now a different date.
            queryClient.invalidateQueries({
                queryKey: OperationsQueryKeys.all,
            });
            queryClient.invalidateQueries({ queryKey: JournalQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
        },
    });
};
